'use client'

import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useState } from 'react'
import { useProductFilters } from '@/hooks/business/catalog'
import type { IProductSortOrder } from '@/schemas/catalog/productFilters'
import FilterOptions, { type IFilterOptionsProps } from '../FilterOptions'

type IFilterListKey = 'feature' | 'size' | 'color'

const sameValue = (a: string, b: string) => a.toLocaleLowerCase('pt-BR') === b.toLocaleLowerCase('pt-BR')

/**
 * Filtro da listagem `/produtos`: barra lateral no desktop e sanfona no mobile, as duas sobre os filtros da URL
 * (`useProductFilters`). A comparação das opções ignora caixa: os links antigos e as tags da PDP mandam `sem pó`,
 * `Sem Pó` ou `látex`, e a API compara as tags sem caixa.
 */
export default function ProductsFilter() {
    const { filters, options, setFilterValues, setPriceRange, setOrder, resetFilters } = useProductFilters()
    const [isCollapsed, setIsCollapsed] = useState(true)

    const isSelected = useCallback(
        (key: IFilterListKey, value: string) => filters[key].some((item) => sameValue(item, value)),
        [filters],
    )

    const onToggle = useCallback(
        (key: IFilterListKey, value: string) => {
            const current = filters[key]
            const next = isSelected(key, value)
                ? current.filter((item) => !sameValue(item, value))
                : [...current, value]
            setFilterValues(key, next)
        },
        [filters, isSelected, setFilterValues],
    )

    const shared: Omit<IFilterOptionsProps, 'idPrefix'> = {
        filters,
        options,
        isSelected,
        onToggle,
        onOrderChange: (order: IProductSortOrder) => setOrder(filters.order === order ? null : order, 'price'),
        onPriceChange: setPriceRange,
        onShortcut: (feature: string) => setFilterValues('feature', [feature]),
        onReset: resetFilters,
    }

    return (
        <>
            <div className="pl-4 max-lg:p-0 sidebar max-xl:hidden basis-1/5" data-testid="products-filter-desktop">
                <div className="min-h-[816px] w-[251px] rounded-full bg-cta-yellow relative pt-6">
                    <span className="px-8 py-2 text-xl text-white rounded-full font-md bg-secondary">Filtre por:</span>
                    <div className="flex flex-col px-10 py-8 sidebar-content">
                        <FilterOptions idPrefix="desktop" {...shared} />
                    </div>
                </div>
            </div>
            <div className="xl:hidden w-full " data-testid="products-filter-mobile">
                <button
                    type="button"
                    className="col-start-1 row-start-1 relative w-full py-4 px-8 min-h-[3.75rem] transition-[background-color] duration-200 ease-[ease-out] text-white bg-secondary peer-checked:text-tertiary rounded-full flex items-center justify-between"
                    onClick={() => setIsCollapsed((collapsed) => !collapsed)}
                    aria-expanded={!isCollapsed}
                >
                    Filtre por:
                    <FontAwesomeIcon icon={isCollapsed ? faArrowDown : faArrowUp} className="text-white max-h-4" />
                </button>
                <div
                    className={`flex flex-col px-10 py-8 -mt-8 sidebar-content rounded-b-[40px] bg-cta-yellow transition-all ease-in-out ${isCollapsed ? 'hidden' : 'block'}`}
                >
                    <FilterOptions idPrefix="mobile" {...shared} />
                </div>
            </div>
        </>
    )
}
